import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

export type ChangeboardingUserInteractions = {
	onCtaActioned: VoidFunction;
	onClosed: VoidFunction;
	onDeclined: VoidFunction;
};

type ChangeboardingChildren = (
	dismiss: VoidFunction,
	// Optional while independently deployed consumers and templates adopt semantic callbacks.
	userInteractions?: ChangeboardingUserInteractions,
) => React.ReactNode;

export type CustomPlacementProps = {
	children: ChangeboardingChildren;
	dismiss: VoidFunction;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: `ct-changeboarding-message`,
};

const placement = {
	createFrontend:
		createPlacementFrontend<CustomRenderer<CustomPlacementProps>['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<CustomPlacementProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<CustomPlacementProps>;
	},
	U
> = placement.createFrontend;

export type RenderProps = {
	children: ChangeboardingChildren;
	defaultRender?: React.ReactNode;
	// Temporary, we're migrating this old name to the new name above (fallbackChildren <-> defaultRender)
	fallbackChildren?: React.ReactNode;
} & EligibleMessage;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<CustomPlacementProps>;
	},
	RenderProps
> = createFrontend<RenderProps>(
	(Messages) =>
		({ children, defaultRender = null, fallbackChildren = null, ...eligibleMessage }) => {
			if (eligibleMessage) {
				return (
					<Messages.render {...eligibleMessage} dismiss={() => {}}>
						{children}
					</Messages.render>
				);
			}

			if (fallbackChildren) {
				return <>{fallbackChildren}</>;
			}

			return <>{defaultRender}</>;
		},
);
